/**
 * Pure helpers for crew scan progress / reconciliation UI.
 * Callers pass server counts + pending-confirmation count; no API I/O here.
 */

export type ScanReconcileCount = {
  barcode: string;
  expected: number;
  scanned: number;
  variance: number;
  status?: string;
};

/** True when any variance, not_on_manifest, wrong_stop, or pending confirmation needs attention. */
export function scanNeedsAttention(
  counts: ScanReconcileCount[],
  pendingCount: number,
): boolean {
  if (pendingCount > 0) return true;
  return counts.some(
    (c) =>
      c.variance !== 0 ||
      c.status === 'not_on_manifest' ||
      c.status === 'wrong_stop' ||
      c.status === 'over' ||
      c.status === 'short',
  );
}

/**
 * True when an exception unblocks the stop, or counts have no attention items.
 * Empty manifest with no pending is treated as reconciled.
 */
export function scanIsReconciled(
  counts: ScanReconcileCount[],
  pendingCount: number,
  hasException: boolean,
): boolean {
  if (hasException) return true;
  return !scanNeedsAttention(counts, pendingCount);
}

/** Sum scanned/expected across manifest lines with expected > 0. */
export function aggregateProgress(counts: ScanReconcileCount[]): {
  scanned: number;
  expected: number;
} {
  let scanned = 0;
  let expected = 0;
  for (const c of counts) {
    if (c.expected > 0) {
      expected += c.expected;
      scanned += c.scanned;
    }
  }
  return { scanned, expected };
}
